import {
  AuthorityType,
  BankAccountIdentifierType,
  PayoutCreditorKind,
  PayoutPurposeCode,
  TransferType
} from '@sollapay/enums';
import {pickDefined} from '@sollapay/utils';

import {isBankCheckAccountType, resolvePayeeKind} from './creditor-account.utils';
import {resolvePurposeLabel} from './purpose-options.utils';
import {resolvePayoutTransferType} from './transfer-type.utils';

import type {CreatePayoutFormValues, ConfirmationStepProps} from '../components';
import type {BankAccountInputType} from './creditor-account.utils';
import type {
  BankTransferType,
  BeneficiaryDTO,
  CreatePayoutInstructionPayload,
  CreditorAccountInput
} from '@sollapay/types';
import type {TFunction} from 'i18next';

function buildCreditorAccount(
  values: CreatePayoutFormValues,
  accountType: BankAccountInputType
): CreditorAccountInput {
  if (accountType === BankAccountIdentifierType.BANK_DETAILS) {
    return {
      inputType: BankAccountIdentifierType.BANK_DETAILS,
      bankCode: values.bankCode,
      branchCode: values.branchCode,
      accountNumber: values.bankAccountNumber
    };
  }
  return {inputType: accountType, accountNumber: values.accountNumber};
}

/** The document, account and rail always travel together — the account-type select picks the rail. A bank check has neither document nor account. */
function buildTransferFields(values: CreatePayoutFormValues):
  | {
      creditorDocumentId: string;
      creditorAccount: CreditorAccountInput;
      transferType: BankTransferType;
    }
  | {transferType: TransferType.BANK_CHECK} {
  const {accountType} = values;
  if (isBankCheckAccountType(accountType)) {
    return {transferType: TransferType.BANK_CHECK};
  }
  return {
    creditorDocumentId: values.creditorDocumentId,
    creditorAccount: buildCreditorAccount(values, accountType),
    transferType: resolvePayoutTransferType(accountType)
  };
}

/**
 * The request shape differs per payee kind: an authority is identified by its code (plus the
 * municipality's name where there is one) rather than a party id, carries a purpose code instead
 * of free text, and the Tax Authority sends a voucher number and no bank account at all.
 */
export function buildCreatePayoutPayload(
  values: CreatePayoutFormValues,
  idempotencyKey: string
): CreatePayoutInstructionPayload {
  const shared = {
    amount: values.amount,
    currency: 'ILS' as const,
    idempotencyKey
  };
  const payeeKind = resolvePayeeKind(values.beneficiaryId);
  const purpose = {
    purposeCode: values.purposeCode as PayoutPurposeCode,
    ...pickDefined({
      purposeOtherDetail:
        values.purposeCode === PayoutPurposeCode.OTHER ? values.purposeOtherDetail : undefined
    })
  };

  switch (payeeKind) {
    case AuthorityType.MUNICIPALITY:
      return {
        ...shared,
        ...purpose,
        creditorKind: PayoutCreditorKind.AUTHORITY,
        authorityType: AuthorityType.MUNICIPALITY,
        municipalityName: values.authorityName,
        paymentReference: values.paymentReference,
        ...buildTransferFields(values)
      };
    case AuthorityType.ISRAEL_LAND_AUTHORITY:
      return {
        ...shared,
        ...purpose,
        creditorKind: PayoutCreditorKind.AUTHORITY,
        authorityType: AuthorityType.ISRAEL_LAND_AUTHORITY,
        paymentReference: values.paymentReference,
        ...buildTransferFields(values)
      };
    case AuthorityType.TAX_AUTHORITY:
      return {
        ...shared,
        ...purpose,
        creditorKind: PayoutCreditorKind.AUTHORITY,
        authorityType: AuthorityType.TAX_AUTHORITY,
        creditorDocumentId: values.creditorDocumentId,
        voucherNumber: values.voucherNumber
      };
    default:
      return {
        ...shared,
        creditorKind: PayoutCreditorKind.BENEFICIARY,
        creditorPartyId: values.beneficiaryId,
        ...buildTransferFields(values),
        description: values.description
      };
  }
}

/** Maps the same form values to the confirmation summary's display strings, per payee kind. */
export function resolveConfirmationProps(params: {
  values: CreatePayoutFormValues;
  selectedBeneficiary: BeneficiaryDTO | undefined;
  t: TFunction<'trustAccounts'>;
  tAuthorities: TFunction<'authorities'>;
}): ConfirmationStepProps {
  const {values, selectedBeneficiary, t, tAuthorities} = params;
  const payeeKind = resolvePayeeKind(values.beneficiaryId);
  const purpose = values.purposeCode
    ? resolvePurposeLabel(
        {purposeCode: values.purposeCode, purposeOtherDetail: values.purposeOtherDetail || null},
        t
      )
    : undefined;

  if (payeeKind === AuthorityType.TAX_AUTHORITY) {
    return {
      payeeName: tAuthorities(`${AuthorityType.TAX_AUTHORITY}.label`),
      amount: values.amount,
      purpose,
      reference: {
        label: t('view.payouts.create.details.voucherNumber'),
        value: values.voucherNumber
      }
    };
  }

  if (
    payeeKind === AuthorityType.MUNICIPALITY ||
    payeeKind === AuthorityType.ISRAEL_LAND_AUTHORITY
  ) {
    const isMunicipality = payeeKind === AuthorityType.MUNICIPALITY;
    return {
      payeeName: isMunicipality ? values.authorityName : tAuthorities(`${payeeKind}.label`),
      payeeType: isMunicipality ? tAuthorities(`${payeeKind}.label`) : undefined,
      amount: values.amount,
      purpose,
      reference: {
        label: t('view.payouts.create.details.paymentReference'),
        value: values.paymentReference
      }
    };
  }

  return {
    payeeName: selectedBeneficiary?.fullName ?? '',
    amount: values.amount,
    payeeType: selectedBeneficiary ? t(`partyRole.${selectedBeneficiary.role}`) : undefined,
    purpose: values.description || undefined
  };
}
